<template>
    <div
        v-if="slides.length"
        class="carousel-product"
    >
        <div class="carousel">
            <div
                ref="container"
                :class="classSlides"
                class="slides"
            >
                <div
                    v-for="(item, index) in slides"
                    :key="index"
                    ref="elements"
                    class="slide"
                >
                    <ProductCard
                        :item="item"
                        :variations="getVariations(item)"
                        :sizes="[
                            'calc(100vw - 80px)',
                            [640, 'calc((100vw - 100px) / 2)'],
                            [1024, 'calc((100vw - 120px) / 3)'],
                            [1140, 'calc((100vw - 140px) / 4)'],
                            [1360, '320px'],
                            [1440, '339px'],
                        ]"
                        page-type="other"
                    />
                </div>
            </div>
        </div>

        <ButtonRound
            :aria-label="t('catalogue.carousel.button_prev')"
            :disabled="isAtStart"
            class="button-round left"
            icon="angle-left"
            @click="previous"
        />

        <ButtonRound
            :aria-label="t('catalogue.carousel.button_next')"
            :disabled="isAtEnd"
            class="button-round right"
            icon="angle-right"
            @click="next"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
    type ProductCardFragment,
    type ComponentCatalogueProductCarouselFragment,
} from '@/graphql/api'
import { assertNever, isNotUndefinedOrNull } from '@/helpers'

import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import ProductCard from '@/components/layout/cards/ProductCard.vue'
import {
    CARAOUSEL_INITIAL_SLIDE_WIDTH,
    useCarousel,
} from '@/components/layout/carousels/base/useCarouselNew.ts'

import { useIsMobile } from '@/plugins/is-mobile.ts'

const { data } = defineProps<{
    data: ComponentCatalogueProductCarouselFragment
}>()

const { t } = useI18n()

const slides = computed(() =>
    data.items
        .map((item) => {
            return item.product
        })
        .filter(isNotUndefinedOrNull)
)

const getVariations = (
    item: NonNullable<
        ComponentCatalogueProductCarouselFragment['items'][number]['product']
    >
): ProductCardFragment[] | undefined => {
    switch (item.__typename) {
        case 'CatalogueEngagementRing':
            return item.variationsByPrimaryMetal
        case 'CatalogueWeddingRing':
        // fallthrough
        case 'CatalogueJewelleryBracelet':
        // fallthrough
        case 'CatalogueJewelleryEarrings':
        // fallthrough
        case 'CatalogueJewelleryNecklace':
        // fallthrough
        case 'CatalogueJewelleryRing':
            return item.variations
        case 'CatalogueReadyToGoRing':
            return undefined
        default:
            return assertNever(item, undefined)
    }
}

// center the slide when only one slide shown at a time (mobile)
const isMobile = useIsMobile(640)

const { container, slideWidth, elements, previous, next, isAtStart, isAtEnd } =
    useCarousel(
        computed(() => slides.value.length),
        { isCentered: isMobile }
    )

const classSlides = computed(() => ({
    // center the slides on desktop if not enough slides to
    // fill the container
    centered:
        elements.value &&
        slideWidth.value &&
        slideWidth.value !== CARAOUSEL_INITIAL_SLIDE_WIDTH &&
        container.value?.offsetWidth &&
        elements.value.length * slideWidth.value <
            (container.value?.offsetWidth || 0),
}))

const scrollButtonTop = computed(() => `${slideWidth.value / 2 - 25}px`)
</script>

<style lang="scss" scoped>
.carousel-product {
    position: relative;
    margin: auto;
    z-index: 10;
    max-width: 1440px;

    @include hover() {
        z-index: 11;
    }

    .carousel {
        width: 100%;

        @include min-width(1360) {
            padding: 0;
        }

        .slides {
            position: relative;
            display: flex;
            overflow-y: hidden;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            scroll-padding: 0 30px;
            padding: 0 30px;
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */

            &::-webkit-scrollbar {
                display: none;
            }

            &::after {
                content: '';
                padding-right: 30px;

                @include min-width(1024) {
                    padding-right: 0;
                }
            }

            @include min-width(1360) {
                padding: 0;
                scroll-padding: 0;
            }

            &.centered {
                justify-content: center;
            }

            .slide {
                position: relative;
                user-select: none;
                scroll-snap-align: start;
                flex: 1 0 100%;
                background: $white;
                padding: 0 10px;

                @include min-width(640) {
                    flex: 1 0 50%;
                    scroll-snap-align: start;
                }

                @include min-width(1024) {
                    flex: 1 0 33.33%;
                }

                @include min-width(1140) {
                    max-width: 340px;
                    flex: 1 0 25%;
                }

                @include min-width(1360) {
                    max-width: 359px;
                }
            }
        }
    }

    .button-round {
        position: absolute;
        z-index: 3;
        top: v-bind(scrollButtonTop);

        &.disabled {
            opacity: 0;
        }

        &.left {
            left: 10px;

            @include min-width(1140) {
                left: 20px;
            }

            @include min-width(1600) {
                left: -15px;
            }
        }

        &.right {
            right: 10px;

            @include min-width(1140) {
                right: 20px;
            }

            @include min-width(1600) {
                right: -15px;
            }
        }
    }
}
</style>
